import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import "@/App.css";

/** Code-splitting: cada tela vira chunk separado (abre mais rápido). */
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Financeiro = lazy(() => import("@/pages/Financeiro"));
const Relatorios = lazy(() => import("@/pages/Relatorios"));
const Comissoes = lazy(() => import("@/pages/Comissoes"));
const Clientes = lazy(() => import("@/pages/Clientes"));
const ClienteDetalhe = lazy(() => import("@/pages/ClienteDetalhe"));
const Agendamentos = lazy(() => import("@/pages/Agendamentos"));
const Procedimentos = lazy(() => import("@/pages/Procedimentos"));
const Pacotes = lazy(() => import("@/pages/Pacotes"));
const Equipe = lazy(() => import("@/pages/Equipe"));
const Estoque = lazy(() => import("@/pages/Estoque"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const Leads = lazy(() => import("@/pages/Leads"));
const Tarefas = lazy(() => import("@/pages/Tarefas"));
const Comunicacao = lazy(() => import("@/pages/Comunicacao"));
const FluxoCaixa = lazy(() => import("@/pages/FluxoCaixa"));
const Fechamento = lazy(() => import("@/pages/Fechamento"));
const Historico = lazy(() => import("@/pages/Historico"));
const Trocas = lazy(() => import("@/pages/Trocas"));
const WhatsApp = lazy(() => import("@/pages/WhatsApp"));
const WhatsAppConfig = lazy(() => import("@/pages/WhatsAppConfig"));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3 p-8">
      <div className="w-10 h-10 rounded-full border-2 border-rose-200 border-t-rose-500 animate-spin" />
      <p className="text-sm text-slate-400">Carregando…</p>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#FDF7F8]">
        <div className="w-10 h-10 rounded-full border-2 border-rose-200 border-t-rose-500 animate-spin" />
        <p className="text-sm text-slate-400">Abrindo…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </Layout>
  );
}

function App() {
  useEffect(() => {
    document.title = "Suleima Estética";
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Protected><Dashboard /></Protected>} />
              <Route path="/financeiro" element={<Protected roles={["admin"]}><Financeiro /></Protected>} />
              <Route path="/fluxo-caixa" element={<Protected roles={["admin"]}><FluxoCaixa /></Protected>} />
              <Route path="/fechamento" element={<Protected roles={["admin"]}><Fechamento /></Protected>} />
              <Route path="/historico" element={<Protected roles={["admin"]}><Historico /></Protected>} />
              <Route path="/relatorios" element={<Protected roles={["admin"]}><Relatorios /></Protected>} />
              <Route path="/comissoes" element={<Protected roles={["admin", "profissional"]}><Comissoes /></Protected>} />
              <Route path="/leads" element={<Protected roles={["admin", "secretaria"]}><Leads /></Protected>} />
              <Route path="/tarefas" element={<Protected><Tarefas /></Protected>} />
              <Route path="/clientes" element={<Protected><Clientes /></Protected>} />
              <Route path="/clientes/:id" element={<Protected><ClienteDetalhe /></Protected>} />
              <Route path="/agendamentos" element={<Protected><Agendamentos /></Protected>} />
              <Route path="/procedimentos" element={<Protected roles={["admin"]}><Procedimentos /></Protected>} />
              <Route path="/pacotes" element={<Protected roles={["admin", "secretaria"]}><Pacotes /></Protected>} />
              <Route path="/estoque" element={<Protected roles={["admin", "secretaria"]}><Estoque /></Protected>} />
              <Route path="/trocas" element={<Protected roles={["admin"]}><Trocas /></Protected>} />
              <Route path="/comunicacao" element={<Protected roles={["admin", "secretaria"]}><Comunicacao /></Protected>} />
              <Route path="/whatsapp" element={<Protected roles={["admin", "secretaria"]}><WhatsApp /></Protected>} />
              <Route path="/whatsapp-config" element={<Protected roles={["admin"]}><WhatsAppConfig /></Protected>} />
              <Route path="/equipe" element={<Protected roles={["admin"]}><Equipe /></Protected>} />
              <Route path="/ajuda" element={<Protected><Ajuda /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
